import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { leadsApi, followupsApi, activitiesApi } from '../api'
import { Lead, Followup, Activity, STAGE_LABELS, STAGE_COLORS, PRIORITY_COLORS } from '../types'
import LeadForm from './LeadForm'
import FollowupForm from './FollowupForm'

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [lead, setLead] = useState<Lead | null>(null)
  const [followups, setFollowups] = useState<Followup[]>([])
  const [activities, setActivities] = useState<Activity[]>([])
  const [loading, setLoading] = useState(true)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showFollowupModal, setShowFollowupModal] = useState(false)
  const [activeTab, setActiveTab] = useState<'info' | 'followups' | 'activity'>('info')

  useEffect(() => {
    if (id) {
      loadLead(parseInt(id))
    }
  }, [id])

  async function loadLead(leadId: number) {
    try {
      setLoading(true)
      const [leadData, followupsData, activitiesData] = await Promise.all([
        leadsApi.get(leadId),
        followupsApi.list({ lead_id: leadId.toString() }),
        activitiesApi.list(leadId),
      ])
      setLead(leadData)
      setFollowups(followupsData)
      setActivities(activitiesData)
    } catch (error) {
      console.error('Failed to load lead:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleStageChange(newStage: string) {
    if (!lead) return
    try {
      const updated = await leadsApi.updateStage(lead.id, newStage)
      setLead(updated)
      await loadLead(lead.id)
    } catch (error) {
      console.error('Failed to update stage:', error)
    }
  }

  async function handleDelete() {
    if (!lead || !confirm('Are you sure you want to delete this lead?')) return
    try {
      await leadsApi.delete(lead.id)
      navigate('/leads')
    } catch (error) {
      console.error('Failed to delete lead:', error)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Loading...</div>
  }

  if (!lead) {
    return <div className="text-center py-8">Lead not found</div>
  }

  const stages = Object.entries(STAGE_LABELS)

  return (
    <div className="pb-20 lg:pb-0">
      {/* Header */}
      <div className="mb-4 lg:mb-6">
        <button
          onClick={() => navigate('/leads')}
          className="text-gray-500 hover:text-gray-700 mb-2 text-sm"
        >
          ← Back to Leads
        </button>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold">{lead.coaching_name}</h1>
            <p className="text-gray-600 mt-1 text-sm lg:text-base">Owner: {lead.owner_name}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowEditModal(true)}
              className="btn-secondary text-sm"
            >
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="btn-danger text-sm"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Pipeline Progress */}
      <div className="card mb-4 lg:mb-6">
        <h2 className="text-base lg:text-lg font-semibold mb-3 lg:mb-4">Pipeline Progress</h2>
        <div className="flex items-center justify-between overflow-x-auto">
          {stages.map(([stage, label], index) => (
            <div key={stage} className="flex items-center">
              <button
                onClick={() => handleStageChange(stage)}
                className={`w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center text-xs lg:text-sm font-medium transition-all flex-shrink-0 ${
                  lead.stage === stage
                    ? 'bg-primary-600 text-white scale-110'
                    : stages.findIndex(([s]) => s === lead.stage) > index
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                {index + 1}
              </button>
              {index < stages.length - 1 && (
                <div className={`w-4 lg:w-8 h-1 ${
                  stages.findIndex(([s]) => s === lead.stage) > index
                    ? 'bg-green-500'
                    : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 overflow-x-auto">
          {stages.map(([stage, label]) => (
            <div key={stage} className="text-xs text-gray-500 text-center w-8 lg:w-10 flex-shrink-0">
              {label.split(' ')[0]}
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4 lg:mb-6">
        <nav className="flex gap-2 lg:gap-4">
          {(['info', 'followups', 'activity'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-2 lg:px-4 border-b-2 font-medium text-xs lg:text-sm transition-colors ${
                activeTab === tab
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'info' ? 'Info' : 
               tab === 'followups' ? `Follow-ups (${followups.length})` : 
               `Activity (${activities.length})`}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <div className="card">
            <h3 className="text-base lg:text-lg font-semibold mb-3 lg:mb-4">Contact Information</h3>
            <div className="space-y-2.5 lg:space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Phone:</span>
                <a href={`tel:${lead.phone}`} className="text-primary-600 hover:text-primary-700 text-sm lg:text-base font-medium">
                  {lead.phone}
                </a>
              </div>
              {lead.whatsapp && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">WhatsApp:</span>
                  <a href={`https://wa.me/${lead.whatsapp}`} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 text-sm lg:text-base font-medium">
                    {lead.whatsapp}
                  </a>
                </div>
              )}
              {lead.email && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">Email:</span>
                  <a href={`mailto:${lead.email}`} className="text-primary-600 hover:text-primary-700 text-sm lg:text-base font-medium truncate ml-4">
                    {lead.email}
                  </a>
                </div>
              )}
              {lead.district && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">District:</span>
                  <span className="text-sm lg:text-base">{lead.district}</span>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <h3 className="text-base lg:text-lg font-semibold mb-3 lg:mb-4">Details</h3>
            <div className="space-y-2.5 lg:space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Stage:</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${STAGE_COLORS[lead.stage]}`}>
                  {STAGE_LABELS[lead.stage]}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Priority:</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${PRIORITY_COLORS[lead.priority]}`}>
                  {lead.priority}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Student Count:</span>
                <span className="text-sm lg:text-base">{lead.student_count}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Lead Source:</span>
                <span className="text-sm lg:text-base">{lead.lead_source}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Created:</span>
                <span className="text-sm lg:text-base">{new Date(lead.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {lead.notes && (
            <div className="card lg:col-span-2">
              <h3 className="text-base lg:text-lg font-semibold mb-3 lg:mb-4">Notes</h3>
              <p className="text-gray-700 whitespace-pre-wrap text-sm lg:text-base">{lead.notes}</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'followups' && (
        <div>
          <div className="flex justify-between items-center mb-3 lg:mb-4">
            <h3 className="text-base lg:text-lg font-semibold">Follow-ups</h3>
            <button
              onClick={() => setShowFollowupModal(true)}
              className="btn-primary text-sm"
            >
              + Add Follow-up
            </button>
          </div>

          {followups.length === 0 ? (
            <div className="card text-center py-6 lg:py-8 text-gray-500 text-sm">
              No follow-ups scheduled yet
            </div>
          ) : (
            <div className="space-y-2 lg:space-y-3">
              {followups.map((followup) => (
                <div key={followup.id} className="card">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base lg:text-lg">
                          {followup.type === 'call' ? '📞' : 
                           followup.type === 'whatsapp' ? '💬' : 
                           followup.type === 'email' ? '📧' : 
                           followup.type === 'meeting' ? '🤝' : '🎯'}
                        </span>
                        <span className="font-medium text-sm lg:text-base capitalize">{followup.type}</span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          followup.status === 'completed' ? 'bg-green-100 text-green-700' :
                          followup.status === 'missed' ? 'bg-red-100 text-red-700' :
                          followup.status === 'rescheduled' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {followup.status}
                        </span>
                      </div>
                      {followup.notes && (
                        <p className="text-gray-600 mt-2 text-sm">{followup.notes}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs lg:text-sm text-gray-500">
                        {new Date(followup.scheduled_date).toLocaleString()}
                      </div>
                      {followup.status === 'pending' && (
                        <button
                          onClick={async () => {
                            await followupsApi.complete(followup.id)
                            await loadLead(lead.id)
                          }}
                          className="text-green-600 hover:text-green-700 text-xs lg:text-sm mt-1"
                        >
                          Mark Complete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'activity' && (
        <div>
          <h3 className="text-base lg:text-lg font-semibold mb-3 lg:mb-4">Activity History</h3>
          
          {activities.length === 0 ? (
            <div className="card text-center py-6 lg:py-8 text-gray-500 text-sm">
              No activity recorded yet
            </div>
          ) : (
            <div className="space-y-2 lg:space-y-3">
              {activities.map((activity) => (
                <div key={activity.id} className="card">
                  <div className="flex items-start gap-2 lg:gap-3">
                    <div className="w-7 h-7 lg:w-8 lg:h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-600 text-xs lg:text-sm">
                        {activity.type === 'call' ? '📞' : 
                         activity.type === 'whatsapp' ? '💬' : 
                         activity.type === 'email' ? '📧' : 
                         activity.type === 'meeting' ? '🤝' : 
                         activity.type === 'demo' ? '🎯' : 
                         activity.type === 'stage_change' ? '🔄' : '📝'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-800 text-sm lg:text-base">{activity.description}</p>
                      <p className="text-xs text-gray-500 mt-0.5 lg:mt-1">
                        {new Date(activity.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <LeadForm
          lead={lead}
          onClose={() => setShowEditModal(false)}
          onSaved={(updatedLead) => {
            setLead(updatedLead)
            setShowEditModal(false)
          }}
        />
      )}

      {/* Follow-up Modal */}
      {showFollowupModal && (
        <FollowupForm
          leadId={lead.id}
          onClose={() => setShowFollowupModal(false)}
          onSaved={() => {
            setShowFollowupModal(false)
            loadLead(lead.id)
          }}
        />
      )}
    </div>
  )
}
